import React, { useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  Space,
  Typography,
  message,
  Divider,
  Alert,
  Progress,
} from "antd";
import {
  UploadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";
import { mcapLogsService } from "./services/mcapLogsService";
import { getApiErrorMessage } from "./api-error";

const { Title, Text } = Typography;
const { Dragger } = Upload;

const UploadRobotLog = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const { authFetch, isAuthenticated } = useAuth();
  const { notifySuccess, notifyError } = useNotification();

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileList, setFileList] = useState([]);

  /**
   * Handle file upload
   */
  const handleUpload = async (values) => {
    if (fileList.length === 0) {
      message.error("Please select an MCAP file to upload");
      return;
    }

    if (!isAuthenticated) {
      notifyError("You must be logged in to upload files");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append("file", fileList[0].originFileObj);
      formData.append("robot_id", values.robot_id);
      formData.append("robot_type", values.robot_type || "");
      formData.append("tasks", values.tasks || "");
      formData.append("objects", values.objects || "");
      formData.append("scene_types", values.scene_types || "");
      formData.append("location", values.location || "");
      formData.append("tags", values.tags || "");

      // Upload the file
      const response = await mcapLogsService.upload(
        authFetch,
        formData,
        (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      );

      notifySuccess("MCAP file uploaded successfully!");
      form.resetFields();
      setFileList([]);
      setUploadProgress(0);

      // Navigate to logs page after successful upload
      setTimeout(() => {
        history.push("/robot-logs");
      }, 1500);
    } catch (error) {
      console.error("Upload error:", error);
      const errorMessage = getApiErrorMessage(error, "Failed to upload file");
      notifyError("Upload failed: " + errorMessage);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle file selection before upload
   */
  const beforeUpload = (file) => {
    // Check file extension
    const isMCAP = file.name.toLowerCase().endsWith(".mcap");
    if (!isMCAP) {
      message.error("You can only upload .mcap files!");
      return Upload.LIST_IGNORE;
    }

    // Check file size (e.g., max 2GB)
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB
    if (file.size > maxSize) {
      message.error("File must be smaller than 2GB!");
      return Upload.LIST_IGNORE;
    }

    setFileList([file]);
    return false; // Prevent automatic upload
  };

  /**
   * Handle file removal
   */
  const handleRemove = () => {
    setFileList([]);
  };

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={3}>Upload Robot MCAP Log File</Title>
            <Text type="secondary">
              Upload MCAP robot log files for analysis and storage. The file
              will be automatically parsed to extract metadata, schemas,
              channels, and message statistics.
            </Text>
          </div>

          <Alert
            message="File Requirements"
            description="Only .mcap files are accepted. Maximum file size is 2GB."
            type="info"
            showIcon
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpload}
            disabled={uploading}
          >
            {/* File Upload */}
            <Form.Item
              label="MCAP File"
              name="file"
              rules={[
                {
                  validator: () => {
                    if (fileList.length === 0) {
                      return Promise.reject(
                        new Error("Please select an MCAP file")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Dragger
                beforeUpload={beforeUpload}
                onRemove={handleRemove}
                fileList={fileList}
                maxCount={1}
                accept=".mcap"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag MCAP file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for a single .mcap file upload. Maximum size: 2GB
                </p>
              </Dragger>
            </Form.Item>

            <Divider>Metadata</Divider>

            {/* Robot ID - Required */}
            <Form.Item
              label="Robot ID"
              name="robot_id"
              rules={[
                { required: true, message: "Please enter the Robot ID" },
              ]}
              tooltip="Unique identifier for the robot that generated this log"
            >
              <Input placeholder="e.g., robot_001" />
            </Form.Item>

            {/* Robot Type */}
            <Form.Item
              label="Robot Type"
              name="robot_type"
              tooltip="Type or model of the robot"
            >
              <Input placeholder="e.g., TurtleBot3, AMR-100" />
            </Form.Item>

            {/* Tasks */}
            <Form.Item
              label="Tasks"
              name="tasks"
              tooltip="Comma-separated list of tasks performed during this recording"
            >
              <Input placeholder="e.g., navigation, pick_and_place, inspection" />
            </Form.Item>

            {/* Objects */}
            <Form.Item
              label="Objects"
              name="objects"
              tooltip="Comma-separated list of objects involved in the recording"
            >
              <Input placeholder="e.g., box, pallet, shelf" />
            </Form.Item>

            {/* Scene Types */}
            <Form.Item
              label="Scene Types"
              name="scene_types"
              tooltip="Comma-separated list of scene types or environments"
            >
              <Input placeholder="e.g., warehouse, indoor, outdoor" />
            </Form.Item>

            {/* Location */}
            <Form.Item
              label="Location"
              name="location"
              tooltip="Physical location where the recording was made"
            >
              <Input placeholder="e.g., Building A, Floor 2, Lab 301" />
            </Form.Item>

            {/* Tags */}
            <Form.Item
              label="Tags"
              name="tags"
              tooltip="Comma-separated tags for easier searching and categorization"
            >
              <Input placeholder="e.g., testing, production, demo" />
            </Form.Item>

            {/* Upload Progress */}
            {uploading && uploadProgress > 0 && (
              <Form.Item>
                <Progress percent={uploadProgress} status="active" />
              </Form.Item>
            )}

            {/* Submit Buttons */}
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<UploadOutlined />}
                  loading={uploading}
                  size="large"
                >
                  {uploading ? "Uploading..." : "Upload File"}
                </Button>
                <Button
                  onClick={() => history.push("/robot-logs")}
                  disabled={uploading}
                  size="large"
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default UploadRobotLog;
